import {
  ShowRowBadges,
  ShowRowBuyButton,
  ShowRowCardWrapper,
  ShowRowComedianName,
  ShowRowDate,
  ShowRowLocation,
  ShowRowShareButton,
  ShowRowTimestampMetadata,
  ShowRowUsersChecking,
} from "@/components/shows";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/ui/tooltip";

import ComedianAvatarWithFollowCircle from "@/components/signin/SuggestedComediansForm/ComedianAvatarWithFollowCircle";
import Link from "next/link";
import { MetadataBadge } from "@punchup/ui-common";
import { MultipleComediansDrawer } from "@punchup/venues/client";
import { ShowRowDirectProps } from "@/components/shows/types";
import { cn } from "@punchup/utils/cn";
import { useShowRowContext } from "@/components/shows/ShowRowContext";

export function ShowRow({ showRef }: ShowRowDirectProps) {
  const {
    shows,
    comedian,
    showcaseComedians,
    isFollowed,
    followedComedianIds,
    getImageUrl,
    hideComedianAvatar,
    hideComedianName,
    location,
    hasBadges,
    hasPresaleCode,
    isComediansDrawerOpen,
    setIsComediansDrawerOpen,
  } = useShowRowContext();

  const shouldApplyOptimizations =
    location === "shows-nearby" || location === "feed";

  const show = shows[0];

  // Determine max avatars to show for desktop
  const maxAvatarsToShow =
    showcaseComedians && showcaseComedians.length > 1 ? 2 : 3;

  return (
    <>
      <div className="flex flex-col">
        <ShowRowCardWrapper>
          <div
            ref={showRef}
            className={cn(
              "grid gap-x-3 items-center",
              hideComedianAvatar
                ? hasBadges || hasPresaleCode
                  ? "grid-cols-[205px_1fr_180px_205px_50px] min-h-[108px] py-2 px-[30px]"
                  : "grid-cols-[205px_1fr_205px_50px] min-h-[108px] py-2 px-[30px]"
                : hasBadges || hasPresaleCode
                  ? "grid-cols-[100px_185px_1fr_160px_170px_36px] py-4 px-6"
                  : "grid-cols-[100px_185px_1fr_170px_36px] py-4 px-6"
            )}
          >
            {/* first column: comedian avatar(s) - conditionally shown */}
            {!hideComedianAvatar && (
              <>
                {showcaseComedians && showcaseComedians.length > 1 ? (
                  <div className="relative flex justify-start items-center">
                    <div className="flex -space-x-4">
                      {showcaseComedians
                        .slice(0, maxAvatarsToShow)
                        .map((com, index) => (
                          <div
                            key={com.id}
                            className="relative flex-shrink-0"
                            style={{ zIndex: maxAvatarsToShow - index }}
                          >
                            <ComedianAvatarWithFollowCircle
                              comedianId={com.id}
                              comedianImgUrl={getImageUrl(com)}
                              comedianName={com.display_name || ""}
                              comedianSlug={com.slug}
                              size="sm"
                              className="relative"
                              isFollowed={followedComedianIds.includes(com.id)}
                              isNotPunchupComedian={!com.is_punchup_verified}
                              hideComedianName
                              hideFollowIndicator={
                                index === maxAvatarsToShow - 1 &&
                                showcaseComedians.length > maxAvatarsToShow
                              }
                            />
                            {index === maxAvatarsToShow - 1 &&
                              showcaseComedians.length > maxAvatarsToShow && (
                                <TooltipProvider>
                                  <Tooltip delayDuration={100}>
                                    <TooltipTrigger asChild>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          setIsComediansDrawerOpen(true);
                                        }}
                                        className="border-[1.5px] border-neutral-300 w-full h-full left-0 top-0 absolute bg-black/40 hover:bg-black/60 rounded-full flex items-center justify-center z-10 transition-colors cursor-pointer"
                                      >
                                        <span className="text-white text-xs font-semibold">
                                          +
                                          {showcaseComedians.length -
                                            maxAvatarsToShow}
                                        </span>
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent
                                      sideOffset={10}
                                      className="text-sm"
                                    >
                                      {showcaseComedians.length} comedians total
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                          </div>
                        ))}
                    </div>
                  </div>
                ) : (
                  <ComedianAvatarWithFollowCircle
                    comedianId={comedian.id}
                    comedianImgUrl={getImageUrl(comedian)}
                    comedianName={comedian.display_name || ""}
                    comedianSlug={comedian.slug}
                    size="lg"
                    hideComedianName
                    isFollowed={isFollowed}
                    isNotPunchupComedian={!comedian.is_punchup_verified}
                  />
                )}
              </>
            )}

            {/* second column: show date and location/city for comedian pages, otherwise date and comedian name */}
            {shouldApplyOptimizations || hideComedianName ? (
              <div className="flex flex-col space-y-1.5">
                <div className="flex flex-col gap-1.5">
                  <ShowRowDate />
                  {!hideComedianName && (
                    <ShowRowComedianName
                      showTitle={show.title}
                      className="text-[16px] font-medium leading-tight line-clamp-1"
                      withTooltip
                    />
                  )}
                  {/* Show location - shows underneath date */}
                  <ShowRowLocation />
                </div>
              </div>
            ) : !hideComedianName ? (
              <div className="flex flex-col space-y-1.5">
                <ShowRowComedianName className="text-sm font-medium" />
              </div>
            ) : null}

            {/* third column: time and location/venue name */}
            <div className="flex flex-col">
              {shouldApplyOptimizations && !hideComedianName ? (
                <ShowRowTimestampMetadata />
              ) : hideComedianName ? (
                <ShowRowTimestampMetadata />
              ) : (
                <p className="text-lg font-semibold">
                  {show.datetime.format("h:mm A")}
                </p>
              )}
            </div>

            {/* fourth column: tags/badges */}
            {(hasBadges || hasPresaleCode) && (
              <div className="flex flex-row justify-start pr-4">
                <div className="flex flex-col items-center gap-y-2 w-full">
                  <ShowRowBadges showId={show.id} />
                  <ShowRowUsersChecking showId={show.id} />
                  {hasPresaleCode && (
                    <p className="normal-case font-normal w-full">
                      <MetadataBadge
                        className="w-full"
                        text={`CODE: ${show.presaleCode.trim()}`}
                      />
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* fifth column: buy button */}
            <ShowRowBuyButton />

            {/* sixth column: actions (e.g. share button) */}
            <div className="flex flex-row justify-end items-center">
              <ShowRowShareButton />
            </div>
          </div>
        </ShowRowCardWrapper>
      </div>

      {/* Multiple comedians drawer for showcase shows */}
      {showcaseComedians && showcaseComedians.length > 1 && (
        <MultipleComediansDrawer
          isOpen={isComediansDrawerOpen}
          setIsOpen={setIsComediansDrawerOpen}
          comedians={showcaseComedians}
          followedComedians={followedComedianIds}
        />
      )}
    </>
  );
}
